import React, { useState, useRef, useEffect } from "react";
import { View, Text, TextInput, StyleSheet } from "react-native";
import { useTranslation } from 'react-i18next';
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";

type Props = {
  onValidationChange?: (valid: boolean) => void;
};

export default function BillPayConfirmPinStep({ onValidationChange }: Props) {
  const [pin, setPin] = useState(["", "", "", ""]);
  const { t } = useTranslation();
  const inputs = useRef<(TextInput | null)[]>([]);
  const originalPin = useSessionStore((s: any) => s.consumerPin);
  const [isMismatch, setIsMismatch] = useState(false);

  const handleChange = (text: string, index: number) => {
    if (/^\d*$/.test(text)) {
      const newPin = [...pin];
      newPin[index] = text;
      setPin(newPin);

      // Move to next box automatically
      if (text && index < 3) {
        inputs.current[index + 1]?.focus();
      }

      // Backspace handling
      if (!text && index > 0) {
        inputs.current[index - 1]?.focus();
      }
    }
  };

  useEffect(() => {
    const joined = pin.join("");
    const complete = joined.length === 4;
    const mismatch = complete && String(originalPin || "") !== joined;
    setIsMismatch(mismatch);
    if (onValidationChange) {
      onValidationChange(complete && !mismatch);
    }
  }, [pin, originalPin, onValidationChange]);

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t("PIN_CONFIRM_TITLE")}</Text>
      <Text style={styles.subtitle}>{t("PIN_CONFIRM_SUBTITLE")}</Text>

      <View style={styles.pinContainer}>
        {pin.map((digit, index) => (
          <TextInput
            key={index}
            testID={`billPayConfirmPinInput${index}`}
            ref={(el) => (inputs.current[index] = el)}
            style={styles.pinInput}
            keyboardType="number-pad"
            maxLength={1}
            secureTextEntry
            value={digit}
            onChangeText={(text) => handleChange(text, index)}
          />
        ))}
      </View>

      {isMismatch && (
        <Text style={styles.error}>pin not matching</Text>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
    container: {
    paddingHorizontal: 24,
    backgroundColor: Colors.white
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500', 
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 32
  },
  pinContainer: {
    flexDirection: "row",
    marginBottom: 87,
  },
  pinInput: {
    width: 56,
    height: 56,
    borderRadius: 8,
    textAlign: "center",
    fontSize: 22,
    fontWeight: "600",
    backgroundColor: Colors.searchCompanyBackground,
    marginRight: 10
  },
  error: {
    color: "red",
    fontSize: 12,
    marginTop: 8,
  },
});
